import React, { useRef, lazy, Suspense, useState, useEffect, useMemo } from 'react'
import { useToast } from '../../hooks/use-toast'
import { Card, CardContent, CardHeader } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { 
  Minimize2, 
  Maximize2, 
  X, 
  Lock, 
  Unlock, 
  GripVertical,
  MessageCircle,
  Calendar,
  StickyNote,
   LayoutGrid,
   Columns,
  Mail,
  Users,
  Check,
  MoreHorizontal,
  Search,
  Filter,
  Plus,
  Expand,
  Shrink,
  Focus,
  Loader2,
  AlertCircle,
  FolderOpen,
  Bot,
  Settings,
  Edit3,
  Trash2
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog'
import { trpc } from '../../lib/trpc'
import { cn } from '../../lib/utils'
import { useUIStore } from '../../stores/ui-store'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger
} from '../ui/context-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'

const KanbanBoard = lazy(() => import('../kanban/KanbanBoard'))
const Chat = lazy(() => import('../chat/Chat'))  
const NotesPanel = lazy(() => import('../notes/NotesPanel'))
const NewCalendarPanel = lazy(() => import('../calendar/NewCalendarPanel'))
const Contacts = lazy(() => import('../contacts/Contacts'))
const WorkspaceAiChat = lazy(() => import('../ai/WorkspaceAiChat'))
const DashboardPanel = lazy(() => import('../dashboard/DashboardPanel'))

import SimpleCreatePanel from '../forms/SimpleCreatePanelEnhanced'


const PanelLoadingSpinner = () => (
  <div className="flex items-center justify-center h-full">
    <Loader2 className="w-6 h-6 animate-spin text-krushr-primary" />
  </div>
)

class PanelErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Panel loading error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="flex items-center justify-center h-full text-gray-500">
          <div className="text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Failed to load panel</p>
            <p className="text-xs mt-1">Try refreshing the page</p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// TODO: Import other components as they're created

interface Panel {
  id: string
  type: string
  title: string
  position_x: number
  position_y: number
  width: number
  height: number
  is_minimized: boolean
  is_locked: boolean
  data: Record<string, any>
}

interface PanelRendererProps {
  panel: Panel
  workspaceId: string
  onRefresh?: () => void
  onFullscreen?: (panelId: string, isFullscreen: boolean) => void
  onFocus?: (panelId: string) => void
}

export default function PanelRenderer({ panel, workspaceId, onRefresh, onFullscreen, onFocus }: PanelRendererProps) {
  const { toast } = useToast()
  const utils = trpc.useUtils()
  const { focusMode, focusedPanelId, toggleFocusMode } = useUIStore()
  const [floatingPanels, setFloatingPanels] = useState<Set<string>>(() => new Set())
  
  const [showCreatePanel, setShowCreatePanel] = useState(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editedTitle, setEditedTitle] = useState(panel.title)
  const [selectedKanbanId, setSelectedKanbanId] = useState<string | null>(panel.data?.kanbanId || null)
  
  // Board management states
  const [showCreateBoardModal, setShowCreateBoardModal] = useState(false)
  const [showRenameBoardModal, setShowRenameBoardModal] = useState(false)
  const [newBoardName, setNewBoardName] = useState('')
  const [renameBoardName, setRenameBoardName] = useState('')

  // Update editedTitle when panel title changes from external updates
  useEffect(() => {
    if (!isEditingTitle) {
      setEditedTitle(panel.title)
    }
  }, [panel.title, isEditingTitle])
  
  const toggleMinimize = trpc.panel.toggleMinimize.useMutation({
    onSuccess: () => {
      utils.panel.list.invalidate({ workspaceId })
      onRefresh?.()
      // Force a layout recalculation after minimize/restore
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
      }, 100)
    },
    onError: (error) => {
      console.error('Minimize toggle failed:', error)
      toast({
        title: "Error",
        description: "Failed to minimize/restore panel",
        variant: "destructive"
      })
    }
  })
  const toggleLock = trpc.panel.toggleLock.useMutation({
    onSuccess: () => {
      utils.panel.list.invalidate({ workspaceId })
      onRefresh?.()
    },
    onError: (error) => {
      console.error('Lock toggle failed:', error)
      toast({
        title: "Error",
        description: "Failed to lock/unlock panel",
        variant: "destructive"
      })
    }
  })

  // Handle floating mode toggle
  const handleToggleFloating = () => {
    setFloatingPanels(prev => {
      const newFloatingPanels = new Set(prev)
      if (newFloatingPanels.has(panel.id)) {
        newFloatingPanels.delete(panel.id)
      } else {
        newFloatingPanels.add(panel.id)
      }
      return newFloatingPanels
    })
  }

  const handleCloseFloating = () => {
    setFloatingPanels(prev => {
      const newFloatingPanels = new Set(prev)
      newFloatingPanels.delete(panel.id)
      return newFloatingPanels
    })
  }

  const isFloating = useMemo(() => floatingPanels.has(panel.id), [floatingPanels, panel.id])
  const toggleFullscreen = trpc.panel.toggleFullscreen.useMutation({
    onSuccess: (updatedPanel) => {
      try {
        const panelData = typeof updatedPanel.data === 'string' ? JSON.parse(updatedPanel.data) : (updatedPanel.data || {})
        // Invalidate cache to ensure UI updates immediately
        utils.panel.list.invalidate({ workspaceId })
        onFullscreen?.(panel.id, panelData.isFullscreen || false)
        onRefresh?.()
        // Force component re-render
        setTimeout(() => {
          utils.panel.list.invalidate({ workspaceId })
        }, 50)
      } catch (error) {
        console.error('Error parsing panel data:', error, updatedPanel)
        // Still invalidate cache even if parsing fails
        utils.panel.list.invalidate({ workspaceId })
        onRefresh?.()
      }
    },
    onError: (error) => {
      console.error('Fullscreen toggle failed:', error)
      toast({
        title: "Error",
        description: "Failed to toggle fullscreen mode",
        variant: "destructive"
      })
    }
  })
  const setFocus = trpc.panel.setFocus.useMutation({
    onSuccess: () => {
      utils.panel.list.invalidate({ workspaceId })
      onRefresh?.()
    }
  })
  const deletePanel = trpc.panel.delete.useMutation({
    onSuccess: () => {
      utils.panel.list.invalidate({ workspaceId })
      onRefresh?.()
    },
    onError: (error) => {
      console.error('Panel deletion failed:', error)
      toast({
        title: "Error",
        description: "Failed to delete panel",
        variant: "destructive"
      })
    }
  })

  const updatePanel = trpc.panel.update.useMutation({
    onSuccess: (_, variables) => {
      utils.panel.list.invalidate({ workspaceId })
      onRefresh?.()
      // Only show toast and reset editing state if we're updating the title
      if (variables.title !== undefined) {
        setIsEditingTitle(false)
        toast({
          title: "Success",
          description: "Panel title updated"
        })
      }
    },
    onError: (error, variables) => {
      console.error('Update failed:', error)
      // Only show error toast and reset if we're updating the title
      if (variables.title !== undefined) {
        toast({
          title: "Error", 
          description: "Failed to update panel title",
          variant: "destructive"
        })
        setEditedTitle(panel.title) // Reset to original title
        setIsEditingTitle(false)
      }
    }
  })

  // Workspace members and teams for assignee selection
  const { data: workspaceMembers = [] } = trpc.user.listWorkspaceMembers.useQuery(
    { workspaceId },
    { enabled: !!workspaceId }
  )
  const { data: teams = [] } = trpc.team.getAll.useQuery(undefined, { enabled: true })

  const getPanelIcon = (type: string) => {
    switch (type) {
      case 'KANBAN':
        return <Columns className="w-4 h-4 text-krushr-coral-red" />
      case 'CHAT':
        return <MessageCircle className="w-4 h-4 text-krushr-coral-red" />
      case 'AI_CHAT':
        return <Bot className="w-4 h-4 text-krushr-coral-red" />
      case 'CALENDAR':
        return <Calendar className="w-4 h-4 text-krushr-coral-red" />
      case 'NOTES':
        return <StickyNote className="w-4 h-4 text-krushr-coral-red" />
      case 'EMAIL':
        return <Mail className="w-4 h-4 text-krushr-coral-red" />
      case 'CONTACTS':
        return <Users className="w-4 h-4 text-krushr-coral-red" />
      case 'DASHBOARD':
        return <LayoutGrid className="w-4 h-4 text-krushr-coral-red" />
      default:
        return <GripVertical className="w-4 h-4 text-krushr-coral-red" />
    }
  }

  // Get all kanbans for workspace (only for kanban panels)
  const { data: kanbans = [] } = trpc.kanban.list.useQuery(
    { workspaceId },
    { enabled: !!workspaceId && panel.type === 'KANBAN' }
  )
  
  // Board management mutations
  const createKanban = trpc.kanban.create.useMutation({
    onSuccess: (newKanban) => {
      utils.kanban.list.invalidate({ workspaceId })
      setSelectedKanbanId(newKanban.id)
      setShowCreateBoardModal(false)
      setNewBoardName('')
      toast({
        title: "Board Created",
        description: `${newKanban.title} has been created successfully`
      })
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create board",
        variant: "destructive"
      })
    }
  })
  
  const updateKanban = trpc.kanban.update.useMutation({
    onSuccess: (updatedKanban) => {
      utils.kanban.list.invalidate({ workspaceId })
      setShowRenameBoardModal(false)
      setRenameBoardName('')
      toast({
        title: "Board Renamed",
        description: `Board renamed to ${updatedKanban.title}`
      })
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to rename board",
        variant: "destructive"
      })
    }
  })
  
  const deleteKanban = trpc.kanban.delete.useMutation({
    onSuccess: () => {
      utils.kanban.list.invalidate({ workspaceId })
      // Select first available board after deletion
      if (kanbans.length > 1) {
        const remainingBoards = kanbans.filter(k => k.id !== selectedKanbanId)
        if (remainingBoards.length > 0) {
          setSelectedKanbanId(remainingBoards[0].id)
        }
      }
      toast({
        title: "Board Deleted",
        description: "The board has been deleted successfully"
      })
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete board",
        variant: "destructive"
      })
    }
  })
  
  // Auto-select first kanban if none selected
  useEffect(() => {
    if (panel.type === 'KANBAN' && !selectedKanbanId && kanbans.length > 0) {
      setSelectedKanbanId(kanbans[0].id)
    }
  }, [panel.type, selectedKanbanId, kanbans])

  const renderPanelContent = () => {
    if (panel.is_minimized) {
      return null
    }

    switch (panel.type) {
      case 'KANBAN':
        const currentKanbanId = selectedKanbanId || panel.data?.kanbanId
        
        if (!currentKanbanId) {
          return (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <Columns className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No Kanban board selected</p>
                <p className="text-xs mt-1">Create a Kanban board first, then edit this panel</p>
              </div>
            </div>
          )
        }
        
        return (
          <div className="h-full">
            <Suspense fallback={<PanelLoadingSpinner />}>
              <div className="h-full animate-in fade-in duration-300">
                <KanbanBoard kanban={{ id: currentKanbanId, workspaceId }} className="h-full" />
              </div>
            </Suspense>
          </div>
        )

      case 'CHAT':
        const chatId = panel.data?.chatId
        return (
          <PanelErrorBoundary>
            <Suspense fallback={<PanelLoadingSpinner />}>
              <Chat threadId={chatId} className="h-full" />
            </Suspense>
          </PanelErrorBoundary>
        )

      case 'AI_CHAT':
        return (
          <PanelErrorBoundary>
            <Suspense fallback={<PanelLoadingSpinner />}>
              <WorkspaceAiChat 
                workspaceId={workspaceId} 
                className="h-full" 
              />
            </Suspense>
          </PanelErrorBoundary>
        )

      case 'CALENDAR':
        return (
          <Suspense fallback={<PanelLoadingSpinner />}>
            <NewCalendarPanel workspaceId={workspaceId} className="h-full" />
          </Suspense>
        )

      case 'NOTES':
        return (
          <Suspense fallback={<PanelLoadingSpinner />}>
            <NotesPanel 
              workspaceId={workspaceId} 
              className="h-full" 
            />
          </Suspense>
        )

      case 'EMAIL':
        return (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Email component coming soon</p>
            </div>
          </div>
        )

      case 'CONTACTS':
        return (
          <Suspense fallback={<PanelLoadingSpinner />}>
            <Contacts workspaceId={workspaceId} className="h-full" />
          </Suspense>
        )

      case 'DASHBOARD':
        return (
          <PanelErrorBoundary>
            <Suspense fallback={<PanelLoadingSpinner />}>
              <DashboardPanel 
                workspaceId={workspaceId} 
                className="h-full"
                panelWidth={panel.width}
                panelHeight={panel.height}
              />
            </Suspense>
          </PanelErrorBoundary>
        )

      default:
        return (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <GripVertical className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Unknown panel type: {panel.type}</p>
            </div>
          </div>
        )
    }
  }

  const handleToggleMinimize = () => {
    toggleMinimize.mutate({ id: panel.id })
  }

  const handleToggleLock = () => {
    toggleLock.mutate({ id: panel.id })
  }

  const handleToggleFullscreen = () => {
    toggleFullscreen.mutate({ id: panel.id })
  }

  const handleDelete = () => {
    if (confirm(`Delete panel "${panel.title}"?`)) {
      deletePanel.mutate({ id: panel.id })
    }
  }

  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!panel.is_locked) {
      setIsEditingTitle(true)
    }
  }

  const handleFocus = (e: React.MouseEvent) => {
    // Don't trigger focus handling for floating panels
    if (isFloating) return
    
    // Don't interfere with input/textarea focus
    const target = e.target as HTMLElement
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.closest('input') || target.closest('textarea')) {
      return
    }
    
    setFocus.mutate({ id: panel.id, focused: true })
    onFocus?.(panel.id)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('.panel-drag-handle')) {
      e.stopPropagation()
    } else if (target.closest('.panel-content')) {
      return
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('.panel-drag-handle')) {
      e.stopPropagation()
    }
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('.panel-drag-handle')) {
      e.stopPropagation()
    }
  }

  const handleTitleSave = () => {
    const trimmedTitle = editedTitle.trim()
    if (trimmedTitle && trimmedTitle !== panel.title) {
      updatePanel.mutate({
        id: panel.id,
        title: trimmedTitle
      })
    } else {
      setIsEditingTitle(false)
      setEditedTitle(panel.title)
    }
  }

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleTitleSave()
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false)
      setEditedTitle(panel.title)
    }
  }

  const handleTitleBlur = () => {
    handleTitleSave()
  }

  // Parse panel data properly - it might be a string
  const panelData = useMemo(() => {
    try {
      return typeof panel.data === 'string' ? JSON.parse(panel.data) : (panel.data || {})
    } catch {
      return {}
    }
  }, [panel.data])
  
  const isFullscreen = panelData?.isFullscreen || false
  const isFocused = panelData?.isFocused || false

  return (
    <Card 
      className={cn(
        "panel-card flex flex-col krushr-panel-bg",
        panel.is_minimized ? "h-8" : "h-full",
        panel.is_locked && "border-krushr-warning/30",
        isFullscreen && "fixed inset-0 z-[9999] m-0 rounded-none shadow-2xl"
      )}
      onClick={handleFocus}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <CardHeader 
        className={cn(
          "panel-drag-handle flex-shrink-0 cursor-move",
          panel.is_minimized ? "flex flex-row items-center space-y-0 p-0 px-2 py-0.5 h-8" : "flex flex-row items-center space-y-0 p-0 px-3 py-1.5",
          panel.is_locked && "cursor-not-allowed"
        )}
        style={{ 
          justifyContent: 'flex-start',
          backgroundColor: 'transparent',
          background: 'transparent'
        }}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="text-krushr-coral-red">
            {getPanelIcon(panel.type)}
          </div>
          {isEditingTitle && !panel.is_minimized ? (
            <Input
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onKeyDown={handleTitleKeyDown}
              onBlur={handleTitleBlur}
              className="h-6 px-1 py-0 text-sm font-medium border-krushr-primary focus:border-krushr-primary focus:ring-1 focus:ring-krushr-primary"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <h3 
              className={cn(
                panel.is_minimized ? "font-medium text-xs truncate" : "font-medium text-sm truncate",
                !panel.is_locked && "cursor-pointer hover:text-krushr-primary transition-colors"
              )}
              onClick={handleTitleClick}
              title={panel.is_locked ? "Panel is locked" : "Click to edit title"}
            >
              {panel.title}
            </h3>
          )}
          {panel.is_locked && (
            <Lock className="w-3 h-3 text-krushr-warning flex-shrink-0" />
          )}
          
        </div>
        
        <div className="flex items-center" style={{ marginLeft: 'auto', gap: '2px' }}>
          {/* Panel assignment dropdown (assign icon in header bar) */}
          {!panel.is_minimized && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="w-auto h-5 px-1.5 hover:bg-gray-100 flex items-center gap-1"
                  title="Assign this panel"
                  id={`panel-assign-trigger-${panel.id}`}
                >
                  <Users className="w-3 h-3" />
                  <span className="hidden md:inline text-[11px]">Assign</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 rounded-2xl">
                <div className="px-3 py-2 text-[11px] text-gray-500">Assign to collaborator</div>
                {workspaceMembers.map((m:any) => (
                  <DropdownMenuItem
                    key={m.id}
                    className="flex items-center gap-2 py-2"
                    onClick={() => {
                      const data = typeof panel.data === 'string' ? JSON.parse(panel.data as any) : (panel.data||{})
                      updatePanel.mutate({ id: panel.id, assigneeUserId: m.id, data: { ...data, assigneeUserId: m.id, assigneeTeamId: null } })
                    }}
                  >
                    <Avatar className="w-4 h-4">
                      <AvatarImage src={m.avatar || undefined} />
                      <AvatarFallback className="text-[9px]">{(m.name||'U').slice(0,1)}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm">{m.name || m.email}</span>
                    {(() => { try { const d = typeof panel.data === 'string' ? JSON.parse(panel.data as any) : (panel.data||{}); return d.assigneeUserId === m.id } catch { return false } })() && (
                      <Check className="w-3 h-3 ml-auto text-krushr-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <div className="px-3 py-2 text-[11px] text-gray-500">Assign to team</div>
                {teams.map((t:any) => (
                  <DropdownMenuItem
                    key={t.id}
                    className="flex items-center gap-2 py-2"
                    onClick={() => {
                      const data = typeof panel.data === 'string' ? JSON.parse(panel.data as any) : (panel.data||{})
                      updatePanel.mutate({ id: panel.id, assigneeTeamId: t.id, data: { ...data, assigneeTeamId: t.id, assigneeUserId: null } })
                    }}
                  >
                    <Users className="w-3 h-3" />
                    <span className="text-sm">{t.name}</span>
                    {(() => { try { const d = typeof panel.data === 'string' ? JSON.parse(panel.data as any) : (panel.data||{}); return d.assigneeTeamId === t.id } catch { return false } })() && (
                      <Check className="w-3 h-3 ml-auto text-krushr-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="py-2 text-red-600 hover:bg-red-50"
                  onClick={() => {
                    const data = typeof panel.data === 'string' ? JSON.parse(panel.data as any) : (panel.data||{})
                    updatePanel.mutate({ id: panel.id, data: { ...data, assigneeUserId: null, assigneeTeamId: null } })
                  }}
                >
                  Remove assignment
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Show assignee avatar(s) in the same header bar with context menu */}
          {(() => { 
            try { 
              const data = typeof panel.data === 'string' ? JSON.parse(panel.data as any) : (panel.data||{})
              const userId = data.assigneeUserId as string | undefined
              const teamId = data.assigneeTeamId as string | undefined
              const user = userId ? (workspaceMembers as any[]).find(m => m.id === userId) : null
              const team = teamId ? (teams as any[]).find(t => t.id === teamId) : null
              if (!user && !team) return null
              const label = user ? (user.name || user.email) : (team?.name || 'Team')
              const initial = user ? (user.name || 'U').slice(0,1) : (team?.name || 'T').slice(0,1)
              const avatarSrc = user ? (user.avatar || undefined) : undefined
              return (
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    <div
                      className="ml-1 mr-1"
                      onClick={(e) => {
                        e.stopPropagation()
                        const el = document.getElementById(`panel-assign-trigger-${panel.id}`)
                        el?.click()
                      }}
                    >
                      <Avatar className="w-5 h-5 ring-1 ring-gray-300 cursor-pointer" title={label}>
                        <AvatarImage src={avatarSrc} />
                        <AvatarFallback className="text-[9px]">{initial}</AvatarFallback>
                      </Avatar>
                    </div>
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem
                      onClick={() => {
                        const d = typeof panel.data === 'string' ? JSON.parse(panel.data as any) : (panel.data||{})
                        updatePanel.mutate({ id: panel.id, data: { ...d, assigneeUserId: null, assigneeTeamId: null } })
                      }}
                    >
                      Remove assignment
                    </ContextMenuItem>
                    <ContextMenuItem
                      onClick={() => {
                        // Open the existing Assign dropdown by simulating a click
                        const header = document.querySelector(`[data-panel-id="${panel.id}"]`)
                      }}
                    >
                      Add/Change assignee…
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              )
            } catch { return null }
          })()}
          {/* Kanban board switcher and management */}
          {panel.type === 'KANBAN' && !panel.is_minimized && (
            <div className="flex items-center gap-1 mr-2">
              {/* Board switcher tags - only show if multiple */}
              {kanbans.length > 1 && kanbans.map((kanban) => (
                <button
                  key={kanban.id}
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedKanbanId(kanban.id)
                    // Persist the selection to panel data
                    updatePanel.mutate({
                      id: panel.id,
                      data: { ...panel.data, kanbanId: kanban.id }
                    })
                  }}
                  className={cn(
                    "px-2 py-0.5 text-[11px] font-normal rounded-md transition-colors duration-200 ease-out",
                    (selectedKanbanId || panel.data?.kanbanId) === kanban.id
                      ? "bg-white/60 text-gray-800 shadow-sm border border-gray-300/40"
                      : "bg-transparent text-gray-500 hover:bg-white/30 hover:text-gray-700 active:bg-white/40"
                  )}
                >
                  {kanban.title}
                </button>
              ))}
              
              {/* Kanban management dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-5 px-1.5 hover:bg-white/20 transition-all duration-200 hover:scale-110 active:scale-95"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Plus className="w-3 h-3 text-gray-500 transition-transform duration-200 hover:rotate-90" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-2xl bg-white/95 backdrop-blur-sm border-gray-200/50">
                  <DropdownMenuItem 
                    className="py-2.5 px-3 cursor-pointer hover:bg-gray-50 focus:bg-gray-50"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowCreateBoardModal(true)
                    }}
                  >
                    <Plus className="w-4 h-4 mr-3 text-gray-600" />
                    <span className="text-sm">Create New Board</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="my-1 bg-gray-200/50" />
                  <DropdownMenuItem 
                    className="py-2.5 px-3 cursor-pointer hover:bg-gray-50 focus:bg-gray-50"
                    onClick={(e) => {
                      e.stopPropagation()
                      const currentBoard = kanbans.find(k => k.id === (selectedKanbanId || panel.data?.kanbanId))
                      if (currentBoard) {
                        setRenameBoardName(currentBoard.title)
                        setShowRenameBoardModal(true)
                      }
                    }}
                  >
                    <Edit3 className="w-4 h-4 mr-3 text-gray-600" />
                    <span className="text-sm">Rename Current Board</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="py-2.5 px-3 cursor-pointer hover:bg-gray-50 focus:bg-gray-50"
                    onClick={(e) => {
                      e.stopPropagation()
                      const currentBoard = kanbans.find(k => k.id === (selectedKanbanId || panel.data?.kanbanId))
                      if (currentBoard) {
                        setRenameBoardName(currentBoard.title)
                        setShowRenameBoardModal(true)
                      }
                    }}
                  >
                    <Settings className="w-4 h-4 mr-3 text-gray-600" />
                    <span className="text-sm">Board Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="my-1 bg-gray-200/50" />
                  <DropdownMenuItem 
                    className="py-2.5 px-3 cursor-pointer text-red-600 hover:bg-red-50 focus:bg-red-50 focus:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation()
                      const currentBoard = kanbans.find(k => k.id === (selectedKanbanId || panel.data?.kanbanId))
                      if (currentBoard && confirm(`Delete board "${currentBoard.title}"? This action cannot be undone.`)) {
                        deleteKanban.mutate({ id: currentBoard.id })
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-3" />
                    <span className="text-sm">Delete Board</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          
          {/* Kanban toolbar toggles - both gear and more do the same thing */}
          {panel.type === 'KANBAN' && !panel.is_minimized && (
            <>
              <Button 
                size="sm" 
                variant="ghost" 
                className="w-5 h-5 p-0 hover:bg-gray-100 flex-shrink-0"
                title="Toggle tools"
                onClick={(e) => {
                  e.stopPropagation()
                  // Find the toolbar toggle within this specific panel
                  const currentPanel = (e.target as HTMLElement).closest('.panel-card')
                  if (currentPanel) {
                    const kanbanBoard = currentPanel.querySelector('[data-kanban-toolbar-toggle]')
                    if (kanbanBoard) {
                      (kanbanBoard as HTMLElement).click()
                    }
                  }
                }}
              >
                <MoreHorizontal className="w-2.5 h-2.5" />
              </Button>
            </>
          )}
          
          {/* Focus mode button */}
          <Button
            size="sm"
            variant="ghost"
            className={cn(
              "w-5 h-5 p-0 hover:bg-gray-100 flex-shrink-0",
              focusMode && focusedPanelId === panel.id && "bg-krushr-primary/10 text-krushr-primary"
            )}
            onClick={() => toggleFocusMode(panel.id)}
            title={focusMode && focusedPanelId === panel.id ? "Exit focus mode" : "Focus on this panel"}
          >
            <Focus className="w-2.5 h-2.5" />
          </Button>
          
          {!panel.is_minimized && (
          <Button 
            size="sm" 
            variant="ghost" 
            className="w-5 h-5 p-0 hover:bg-gray-100 flex-shrink-0"
            onClick={handleToggleLock}
            disabled={toggleLock.isPending}
            title={panel.is_locked ? "Unlock panel" : "Lock panel"}
          >
            {toggleLock.isPending ? (
              <div className="w-2.5 h-2.5 border border-gray-400 border-t-transparent rounded-full animate-spin" />
            ) : panel.is_locked ? (
              <Unlock className="w-2.5 h-2.5" />
            ) : (
              <Lock className="w-2.5 h-2.5" />
            )}
          </Button>
          )}

          {!panel.is_minimized && (
          <Button 
            size="sm" 
            variant="ghost" 
            className="w-5 h-5 p-0 hover:bg-blue-100 hover:text-blue-600 flex-shrink-0"
            onClick={handleToggleFullscreen}
            disabled={toggleFullscreen.isPending}
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {toggleFullscreen.isPending ? (
              <div className="w-2.5 h-2.5 border border-gray-400 border-t-transparent rounded-full animate-spin" />
            ) : isFullscreen ? (
              <Shrink className="w-2.5 h-2.5" />
            ) : (
              <Expand className="w-2.5 h-2.5" />
            )}
          </Button>
          )}
          
          <Button 
            size="sm" 
            variant="ghost" 
            className="w-5 h-5 p-0 hover:bg-gray-100 flex-shrink-0"
            onClick={handleToggleMinimize}
            disabled={toggleMinimize.isPending}
            title={panel.is_minimized ? "Restore panel" : "Minimize panel"}
          >
            {toggleMinimize.isPending ? (
              <div className="w-2.5 h-2.5 border border-gray-400 border-t-transparent rounded-full animate-spin" />
            ) : panel.is_minimized ? (
              <Maximize2 className="w-2.5 h-2.5" />
            ) : (
              <Minimize2 className="w-2.5 h-2.5" />
            )}
          </Button>
          
          {!panel.is_minimized && (
          <Button 
            size="sm" 
            variant="ghost" 
            className="w-5 h-5 p-0 hover:bg-red-100 hover:text-red-600 flex-shrink-0"
            onClick={handleDelete}
            title="Delete panel"
          >
            <X className="w-2.5 h-2.5" />
          </Button>
          )}
        </div>
      </CardHeader>
      
      {!panel.is_minimized && (
        <CardContent className="panel-content flex-1 p-1 pt-0 overflow-hidden">
          {renderPanelContent()}
        </CardContent>
      )}
      
      {/* Task Creation Panel */}
      {showCreatePanel && (
        <SimpleCreatePanel
          workspaceId={workspaceId}
          kanbanColumnId={panel.type === 'KANBAN' && panel.data?.kanbanId ? panel.data.kanbanId : undefined}
          open={showCreatePanel}
          onClose={() => setShowCreatePanel(false)}
          onSuccess={() => {
            setShowCreatePanel(false)
            onRefresh?.()
          }}
        />
      )}
      
      {/* Create Board Modal */}
      <Dialog open={showCreateBoardModal} onOpenChange={setShowCreateBoardModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Board</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Board Name</label>
              <Input
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                placeholder="Enter board name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newBoardName.trim()) {
                    createKanban.mutate({
                      title: newBoardName.trim(),
                      description: '',
                      workspaceId
                    })
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateBoardModal(false)
                setNewBoardName('')
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (newBoardName.trim()) {
                  createKanban.mutate({
                    title: newBoardName.trim(),
                    description: '',
                    workspaceId
                  })
                }
              }}
              disabled={!newBoardName.trim() || createKanban.isPending}
            >
              {createKanban.isPending ? 'Creating...' : 'Create Board'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Rename Board Modal */}
      <Dialog open={showRenameBoardModal} onOpenChange={setShowRenameBoardModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Board</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Board Name</label>
              <Input
                value={renameBoardName}
                onChange={(e) => setRenameBoardName(e.target.value)}
                placeholder="Enter new board name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && renameBoardName.trim()) {
                    const currentBoardId = selectedKanbanId || panel.data?.kanbanId
                    if (currentBoardId) {
                      updateKanban.mutate({
                        id: currentBoardId,
                        title: renameBoardName.trim()
                      })
                    }
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRenameBoardModal(false)
                setRenameBoardName('')
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const currentBoardId = selectedKanbanId || panel.data?.kanbanId
                if (currentBoardId && renameBoardName.trim()) {
                  updateKanban.mutate({
                    id: currentBoardId,
                    title: renameBoardName.trim()
                  })
                }
              }}
              disabled={!renameBoardName.trim() || updateKanban.isPending}
            >
              {updateKanban.isPending ? 'Renaming...' : 'Rename Board'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}